import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, UserPlus, Search, Check, X, Flame, Zap } from "lucide-react";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

function PersonRow({ p, children }) {
  return (
    <div className="flex items-center gap-3 p-3.5" data-testid={`friend-row-${p.user_id}`}>
      <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold text-white overflow-hidden shrink-0">
        {p.picture ? <img src={p.picture} alt={p.name} loading="lazy" className="w-full h-full object-cover" /> : p.name?.[0]}
      </div>
      <Link to={`/u/${p.user_id}`} className="flex-1 min-w-0">
        <p className="text-sm text-white truncate hover:text-indigo-300">{p.name}</p>
        <p className="text-[11px] text-slate-400 flex items-center gap-2">
          <span className="flex items-center gap-1"><Zap className="w-3 h-3 text-[#FBBF24]" />{p.xp} XP</span>
          <span className="flex items-center gap-1"><Flame className="w-3 h-3 text-indigo-400" />{p.streak_current}</span>
        </p>
      </Link>
      <div className="flex items-center gap-2 shrink-0">{children}</div>
    </div>
  );
}

export default function FriendsPage() {
  const [tab, setTab] = useState("friends");
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const loadFriends = () => api.get("/friends").then((r) => setFriends(r.data));
  const loadRequests = () => api.get("/friends/requests").then((r) => setRequests(r.data));

  useEffect(() => { loadFriends(); loadRequests(); }, []);

  const runSearch = async (q) => {
    setQuery(q);
    if (q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const { data } = await api.get(`/friends/search?q=${encodeURIComponent(q.trim())}`);
      setResults(data);
    } finally {
      setSearching(false);
    }
  };

  const sendRequest = async (userId) => {
    try {
      await api.post(`/friends/request/${userId}`);
      toast.success("Friend request sent!");
      setResults((prev) => prev.map((r) => (r.user_id === userId ? { ...r, friend_status: "pending_sent" } : r)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send request");
    }
  };

  const acceptRequest = async (friendshipId) => {
    await api.post(`/friends/${friendshipId}/accept`);
    toast.success("Friend added!");
    loadRequests(); loadFriends();
  };

  const rejectRequest = async (friendshipId) => {
    await api.post(`/friends/${friendshipId}/reject`);
    loadRequests();
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="friends-page">
      <div className="flex items-center gap-3 mb-2">
        <Users className="w-6 h-6 text-indigo-400" />
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-400">Community</span>
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white">Friends</h1>
      <p className="text-sm text-slate-400 mt-2">Connect with other learners and compete on the friends-only Duel leaderboard.</p>

      <div className="relative mt-6">
        <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
        <input
          value={query} onChange={(e) => runSearch(e.target.value)} placeholder="Search learners by name..."
          data-testid="friends-search-input"
          className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-400"
        />
      </div>
      {query.trim().length >= 2 && (
        <div className="glass-panel rounded-2xl divide-y divide-white/5 mt-3" data-testid="friends-search-results">
          {searching && <p className="p-4 text-xs text-slate-400 text-center">Searching...</p>}
          {!searching && results.length === 0 && <p className="p-4 text-xs text-slate-400 text-center">No learners found.</p>}
          {results.map((p) => (
            <PersonRow key={p.user_id} p={p}>
              {p.friend_status === "none" && (
                <button onClick={() => sendRequest(p.user_id)} data-testid={`add-friend-btn-${p.user_id}`} className="flex items-center gap-1.5 text-xs font-semibold bg-indigo-500/15 hover:bg-indigo-500/25 text-indigo-300 px-3 py-1.5 rounded-lg">
                  <UserPlus className="w-3.5 h-3.5" /> Add
                </button>
              )}
              {p.friend_status === "pending_sent" && <span className="text-xs text-slate-400">Request Sent</span>}
              {p.friend_status === "pending_received" && <span className="text-xs text-amber-400">Respond in Requests tab</span>}
              {p.friend_status === "friends" && <span className="text-xs text-emerald-400">Friends</span>}
            </PersonRow>
          ))}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList data-testid="friends-tabs">
          <TabsTrigger value="friends" data-testid="friends-tab-friends">My Friends ({friends.length})</TabsTrigger>
          <TabsTrigger value="requests" data-testid="friends-tab-requests">Requests {requests.length > 0 && `(${requests.length})`}</TabsTrigger>
        </TabsList>

        <TabsContent value="friends" className="mt-6">
          <div className="glass-panel rounded-2xl divide-y divide-white/5" data-testid="friends-list">
            {friends.length === 0 && <p className="p-6 text-sm text-slate-400 text-center">No friends yet. Search above to add some!</p>}
            {friends.map((f) => <PersonRow key={f.user_id} p={f} />)}
          </div>
        </TabsContent>

        <TabsContent value="requests" className="mt-6">
          <div className="glass-panel rounded-2xl divide-y divide-white/5" data-testid="friends-requests-list">
            {requests.length === 0 && <p className="p-6 text-sm text-slate-400 text-center">No pending friend requests.</p>}
            {requests.map((r) => (
              <PersonRow key={r.friendship_id} p={r}>
                <button onClick={() => acceptRequest(r.friendship_id)} data-testid={`accept-request-btn-${r.friendship_id}`} className="p-1.5 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-400"><Check className="w-4 h-4" /></button>
                <button onClick={() => rejectRequest(r.friendship_id)} data-testid={`reject-request-btn-${r.friendship_id}`} className="p-1.5 rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-400"><X className="w-4 h-4" /></button>
              </PersonRow>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
